// Wires a show <select> to the controlled v0.5 vocabulary: options come from
// getShows() (defaults ∪ custom ∪ booth-seen) plus a trailing "+ Add show…"
// action. Picking that action prompts for a name, validates it (non-empty, no
// case-insensitive duplicate), persists it, and selects it — there is NO
// free-typing into the show field, which is what preserves the WWIN/WINN typo fix.
// Shared by the capture and detail screens so both dropdowns stay in sync.

import { listBoothShows } from '../db/booths';
import { getShows, addShow } from '../config/shows';
import { toast } from './toast';
import { promptDialog } from './confirm';

const ADD_SENTINEL = '__add_show__';

/** Populate the select and wire the "+ Add show…" action. Async (reads booths). */
export async function populateShowSelect(select: HTMLSelectElement, selected: string): Promise<void> {
  const boothShows = await listBoothShows();
  render(select, getShows(boothShows), selected);
  let last = select.value;

  select.addEventListener('change', async () => {
    if (select.value !== ADD_SENTINEL) {
      last = select.value;
      return;
    }
    // "+ Add show…" chosen — prompt (styled modal), validate, persist, re-select.
    const input = await promptDialog({
      title: 'Add a show',
      message: 'Enter a new show name. It’ll be available in the dropdown from now on.',
      placeholder: 'e.g. Sourcing at MAGIC',
      confirmLabel: 'Add',
    });
    const shows = await listBoothShows();
    if (input == null || !input.trim()) {
      select.value = last; // cancelled / empty — revert
      return;
    }
    const res = addShow(input, shows);
    if (!res.ok) {
      toast(res.error ?? 'Could not add show', 'warn');
      select.value = last;
      return;
    }
    render(select, getShows(shows), res.name!);
    last = select.value;
  });
}

function render(select: HTMLSelectElement, shows: string[], selected: string): void {
  const opts = shows
    .map((s) => `<option value="${esc(s)}">${esc(s)}</option>`)
    .join('');
  select.innerHTML = `${opts}<option value="${ADD_SENTINEL}">+ Add show…</option>`;
  // Apply the desired selection if present; else fall back to the first option.
  select.value = shows.some((s) => s === selected) ? selected : (shows[0] ?? '');
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
